import type { IconName } from '@/design-system';
import { hasRole, isPlatformAdmin, type Role, type SessionAccess } from '@/shared/api';

import { paths } from '../router/paths';
import { routeRoles } from '../router/roles';

/**
 * De navigatie, als gegevens.
 *
 * Twee groepen met een kop in mono - VANDAAG en WERK - en onderaan een
 * naamloze groep met Instellingen en Uitloggen. Een platformbeheerder krijgt daar
 * PLATFORM bij.
 *
 * **De rail is per rol altijd hetzelfde.** Er staan alleen bestemmingen in die
 * altijd bestaan. Wat bij één keuring hoort - opname, energielabel, overzicht,
 * rapport, klantpagina - staat in de kop van die keuring, als tabbladen (zie
 * `order-tabs.ts`). In het prototype stonden die schermen in de rail, omdat daar
 * altijd een keuring gekozen was; in de app verschenen ze pas als je een keuring
 * opende en verdwenen ze weer, met meervoudige namen ("Dossiers") voor één keuring.
 *
 * De rollen per item komen uit `router/roles.ts`, dezelfde tabel als die van de
 * router: de rail mag nooit een link tonen waar de router je meteen weer
 * vandaan stuurt.
 */

/**
 * Welke teller er rechts in het item staat. Zie `RailCounts`.
 *
 * Alleen tellers met een bron: het aantal keuringen komt uit `GET /orders`
 * (`meta.total`). "Stops vandaag" en "rapporten die wachten" stonden hier ook,
 * maar zonder bron verschenen ze nooit.
 */
export type RailCountKey = 'inspections';

export type RailCountTone = 'accent' | 'neutral' | 'amber';

export interface RailCount {
    key: RailCountKey;
    tone: RailCountTone;
    /** Voorleestekst; "3" alleen zegt een schermlezer niets. */
    describe: (value: number) => string;
}

export interface NavItemModel {
    key: string;
    label: string;
    to: string;
    /** Het lijn-icoon van het merkblad dat voor het label staat. */
    icon: IconName;
    /** Alleen actief bij een exacte match - nodig voor `/` en `/admin`. */
    end?: boolean;
    roles?: readonly Role[];
    count?: RailCount;
}

export interface NavGroupModel {
    key: string;
    /** De kop in mono-kapitalen. De groep onderaan de rail heeft er geen en
     *  staat daarom niet in deze lijst; zie `buildFooterItems`. */
    label: string;
    items: NavItemModel[];
}

export interface NavContext {
    session: SessionAccess | null;
    /**
     * Werkt deze sessie in een omgeving (eigen token of meekijken)? Zonder
     * omgeving - een platformbeheerder die nergens meekijkt - zijn er geen
     * schermen van een keuringsbedrijf om naartoe te gaan.
     */
    workspace?: boolean;
}

const COUNTS: Record<RailCountKey, RailCount> = {
    inspections: {
        key: 'inspections',
        tone: 'neutral',
        describe: (value) => `${value} ${value === 1 ? 'keuring' : 'keuringen'}`,
    },
};

/** Bouwt de groepen voor deze gebruiker, in de volgorde van het ontwerp. */
export function buildNavGroups({ session, workspace = true }: NavContext): NavGroupModel[] {
    const tenantGroups: NavGroupModel[] = [
        {
            key: 'vandaag',
            label: 'Vandaag',
            items: [
                {
                    key: 'dashboard',
                    label: 'Dashboard',
                    to: paths.dashboard,
                    icon: 'dashboard',
                    end: true,
                },
                {
                    key: 'my-day',
                    label: 'Mijn dag',
                    to: paths.myDay,
                    icon: 'myDay',
                    roles: routeRoles.myDay,
                },
                {
                    key: 'planning',
                    label: 'Planning',
                    to: paths.planning,
                    icon: 'planning',
                    roles: routeRoles.planning,
                },
            ],
        },
        {
            key: 'werk',
            label: 'Werk',
            items: [
                /* Zonder `end`: binnen een keuring (`/keuringen/…`) blijft Keuringen
                   oplichten, dus je ziet altijd waar je bent. */
                {
                    key: 'inspections',
                    label: 'Keuringen',
                    to: paths.inspections,
                    icon: 'inspections',
                    count: COUNTS.inspections,
                },
                {
                    key: 'billing',
                    label: 'Facturatie',
                    to: paths.billing,
                    icon: 'billing',
                    roles: routeRoles.billing,
                },
            ],
        },
    ];
    const groups = workspace ? tenantGroups : [];

    /* `hasRole` laat een platformbeheerder overal binnen, dus die vraag stellen we
       hier rechtstreeks: de PLATFORM-groep is alleen voor wie het écht is. */
    if (isPlatformAdmin(session)) {
        groups.push({
            key: 'platform',
            label: 'Platform',
            items: [
                {
                    key: 'admin-overview',
                    label: 'Overzicht',
                    to: paths.admin,
                    icon: 'platformOverview',
                    end: true,
                },
                /* Zonder `end`: het detail van een bedrijf licht dit item ook op. */
                {
                    key: 'admin-tenants',
                    label: 'Keuringsbedrijven',
                    to: paths.adminTenants,
                    icon: 'tenants',
                },
                {
                    key: 'admin-users',
                    label: 'Gebruikers',
                    to: paths.adminUsers,
                    icon: 'platformUsers',
                },
                {
                    key: 'admin-templates',
                    label: 'Vragenlijsten',
                    to: paths.adminTemplates,
                    icon: 'questionnaires',
                },
                { key: 'admin-audit', label: 'Audit log', to: paths.adminAudit, icon: 'auditLog' },
                {
                    key: 'admin-integrations',
                    label: 'Koppelingen',
                    to: paths.adminIntegrations,
                    icon: 'integrations',
                },
            ],
        });
    }

    return groups.map(keepAllowed(session)).filter((group) => group.items.length > 0);
}

/** Instellingen staat los onderaan, boven Uitloggen en het gebruikersblok. */
export function buildFooterItems(session: SessionAccess | null, workspace = true): NavItemModel[] {
    if (!workspace) {
        return [];
    }

    const items: NavItemModel[] = [
        {
            key: 'settings',
            label: 'Instellingen',
            to: paths.settings,
            icon: 'settings',
            roles: routeRoles.settings,
        },
    ];

    return items.filter((item) => maySee(session, item));
}

function keepAllowed(session: SessionAccess | null) {
    return (group: NavGroupModel): NavGroupModel => ({
        ...group,
        items: group.items.filter((item) => maySee(session, item)),
    });
}

function maySee(session: SessionAccess | null, item: NavItemModel): boolean {
    return !item.roles || hasRole(session, item.roles);
}
